import {
  Component,
  HostListener,
  Inject,
  PLATFORM_ID,
  OnDestroy,
  signal,
  ViewChild,
  ElementRef,
  effect,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  Router,
  RouterLink,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  Event as RouterEvent,
} from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { ProjectService } from '../services/project.service';
import { ImageFallbackService } from '../services/image-fallback.service';
import { NgOptimizedImage } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-banner',
  standalone: true,
  imports: [FormsModule, RouterLink, NgOptimizedImage, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './banner.component.html',
})
export class BannerComponent implements OnDestroy {
  searchQuery = signal<string>('');
  isProfileSidebarOpen = signal<boolean>(false);
  loggedInUsername = signal<string | undefined>(undefined);
  profilePicture = signal<string>('');
  isSearchExpanded = signal<boolean>(false);
  isNavigating = signal<boolean>(false);

  private destroy$ = new Subject<void>();
  private isBrowser: boolean;

  constructor(
    public authService: AuthService,
    public userService: UserService,
    private projectService: ProjectService,
    public imageFallback: ImageFallbackService,
    private router: Router,
    private translate: TranslateService,
    private destroyRef: DestroyRef,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.profilePicture.set(this.imageFallback.getFallbackImage('profile'));

    effect(() => {
      const profile = this.userService.userProfile();
      if (profile) {
        this.loggedInUsername.set(profile.username);
        this.profilePicture.set(profile.optimizedProfile || '');
      }
    });

    this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: RouterEvent) => {
        if (event instanceof NavigationStart) {
          this.isNavigating.set(true);
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          this.isNavigating.set(false);
        }
      });
  }

  ngOnInit() {
    if (!this.authService.checkAuth()) return;

    if (!this.userService.userProfile()) {
      this.userService.getUserProfile().subscribe();
    }
  }

  @ViewChild('profileSidebar') profileSidebar?: ElementRef;
  @ViewChild('profileImage') profileImage?: ElementRef;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (
      this.isProfileSidebarOpen() &&
      this.profileSidebar?.nativeElement &&
      !this.profileSidebar.nativeElement.contains(event.target) &&
      this.profileImage?.nativeElement &&
      !this.profileImage.nativeElement.contains(event.target)
    ) {
      this.closeProfileSidebar();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    if (this.isProfileSidebarOpen()) {
      this.closeProfileSidebar();
    }
  }

  onLogoClick() {
    if (this.router.url === '/') {
      this.projectService.clearHomeState();
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/']);
      });
    } else {
      this.router.navigate(['/']);
    }
  }

  onSearch(): void {
    const query = this.searchQuery().trim();
    if (query) {
      this.isSearchExpanded.set(false);
      this.router.navigate(['/search'], { queryParams: { q: query } });
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onSearch();
    }
  }

  toggleProfileSidebar() {
    this.isProfileSidebarOpen.update((open) => !open);
    if (
      this.isBrowser &&
      this.isProfileSidebarOpen() &&
      this.profileSidebar?.nativeElement
    ) {
      (
        this.profileSidebar.nativeElement.querySelector('a') as HTMLElement
      )?.focus();
    }
  }

  closeProfileSidebar() {
    this.isProfileSidebarOpen.set(false);
    if (this.isBrowser && this.profileImage?.nativeElement) {
      try {
        this.profileImage.nativeElement.focus();
      } catch (e) {
        // ignore
      }
    }
  }

  navigateAndClose(route: string) {
    this.router.navigate([route]);
    this.closeProfileSidebar();
  }

  toggleSearchExpanded() {
    this.isSearchExpanded.update((v) => !v);
    if (this.isSearchExpanded() && this.isBrowser) {
      setTimeout(() => {
        const input = document.getElementById('mobile-search-input');
        if (input) input.focus();
      }, 100);
    }
  }

  closeSearchExpanded() {
    this.isSearchExpanded.set(false);
  }

  navigateProfileOrSidebar() {
    if (window.innerWidth < 640) {
      this.toggleProfileSidebar();
    } else {
      this.router.navigate(['@' + this.loggedInUsername()]);
    }
  }

  confirmLogout() {
    if (!this.isBrowser) {
      this.logout();
      return;
    }

    if (confirm(this.translate.instant('BANNER.CONFIRM_LOGOUT'))) {
      this.logout();
    }
  }

  logout() {
    this.closeProfileSidebar();
    this.authService
      .logout()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        () => this.router.navigate(['/login']),
        (error) => console.error('[Banner] Error en logout:', error),
      );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
